import { useCallback } from 'react';
import { bookingsAPI } from '../../services/api';
import { transformBookingToAPI, transformBookingFromAPI } from './bookingUtils';

export function useBookingActions({ dispatch, useAPI, activeFacility, getGroundById, getServiceById, calculateSlotPrice }) {
  const addBooking = useCallback(
    async (booking) => {
      try {
        const ground = getGroundById(booking.groundId);
        const service = getServiceById(booking.groundId, booking.serviceId);

        let totalAmount;
        if (booking.totalAmount !== undefined && booking.totalAmount !== null) {
          totalAmount = booking.totalAmount;
        } else {
          totalAmount = calculateSlotPrice(booking.groundId, booking.serviceId, booking.slotId, booking.customPrice);
        }

        const advanceAmount = booking.advanceAmount !== undefined && booking.advanceAmount !== null ? booking.advanceAmount : 0;
        const balanceAmount =
          booking.balanceAmount !== undefined && booking.balanceAmount !== null
            ? booking.balanceAmount
            : Math.max(0, totalAmount - advanceAmount);
        const paymentStatus = balanceAmount > 0 ? 'pending' : 'fully_paid';
        const slotName = booking.slotName || null;
        const timeSlot = booking.timeSlot || null;
        const baseAmount = booking.baseAmount !== undefined && booking.baseAmount !== null ? booking.baseAmount : totalAmount;
        const discountAmount =
          booking.discountAmount !== undefined && booking.discountAmount !== null ? booking.discountAmount : 0;

        const bookingData = {
          ...booking,
          groundName: booking.groundName || ground?.name || 'Ground',
          serviceName: booking.serviceName || service?.name || 'Service',
          slotName,
          timeSlot,
          totalAmount,
          advanceAmount,
          balanceAmount,
          baseAmount,
          discountAmount,
          paymentStatus,
          bookedBy: booking.contactName || booking.bookedBy,
          bookedByPhone: booking.contactPhone || booking.bookedByPhone,
          paymentMode: booking.paymentMode || booking.paymentDetails?.paymentMode || 'cash',
          paymentDetails: {
            ...(booking.paymentDetails || {}),
            baseAmount,
            discountAmount,
            totalAmount,
            advanceAmount,
            paymentMode: booking.paymentMode || booking.paymentDetails?.paymentMode || 'cash',
          },
        };

        if (useAPI) {
          const apiBookingData = transformBookingToAPI(bookingData);
          const response = await bookingsAPI.create(apiBookingData);
          if (response.success && response.data) {
            const apiBooking = transformBookingFromAPI(response.data);
            const newBooking = {
              ...apiBooking,
              date: apiBooking.date || bookingData.date,
              timeSlot: apiBooking.timeSlot || bookingData.timeSlot,
              slotName: apiBooking.slotName || bookingData.slotName,
              slotId: apiBooking.slotId || bookingData.slotId,
              totalAmount: apiBooking.totalAmount || bookingData.totalAmount,
              advanceAmount: apiBooking.advanceAmount !== undefined ? apiBooking.advanceAmount : bookingData.advanceAmount,
              balanceAmount: apiBooking.balanceAmount !== undefined ? apiBooking.balanceAmount : bookingData.balanceAmount,
              baseAmount: apiBooking.baseAmount || bookingData.baseAmount,
              discountAmount: apiBooking.discountAmount !== undefined ? apiBooking.discountAmount : bookingData.discountAmount,
              serviceName: apiBooking.serviceName || bookingData.serviceName,
              groundName: apiBooking.groundName || bookingData.groundName,
              serviceType: apiBooking.serviceType || bookingData.serviceType,
              contactName: apiBooking.contactName || bookingData.contactName,
              contactPhone: apiBooking.contactPhone || bookingData.contactPhone,
              contactEmail: apiBooking.contactEmail || bookingData.contactEmail,
              teamDetails: apiBooking.teamDetails || bookingData.teamDetails,
              guestList: apiBooking.guestList || bookingData.guestList,
              facilityLatitude: activeFacility?.latitude ?? undefined,
              facilityLongitude: activeFacility?.longitude ?? undefined,
              paymentDetails: {
                ...(apiBooking.paymentDetails || {}),
                ...(bookingData.paymentDetails || {}),
                baseAmount: apiBooking.baseAmount || bookingData.baseAmount,
                discountAmount: apiBooking.discountAmount !== undefined ? apiBooking.discountAmount : bookingData.discountAmount,
                totalAmount: apiBooking.totalAmount || bookingData.totalAmount,
                advanceAmount: apiBooking.advanceAmount !== undefined ? apiBooking.advanceAmount : bookingData.advanceAmount,
              },
            };
            dispatch({ type: 'ADD_BOOKING', payload: newBooking });
            return newBooking;
          }
          throw new Error(response.message || 'Failed to create booking');
        }

        const newBooking = {
          ...bookingData,
          id: Date.now(),
          status: 'confirmed',
          facilityLatitude: activeFacility?.latitude ?? undefined,
          facilityLongitude: activeFacility?.longitude ?? undefined,
          paymentDetails: {
            ...(bookingData.paymentDetails || {}),
            baseAmount: bookingData.baseAmount,
            discountAmount: bookingData.discountAmount,
            totalAmount: bookingData.totalAmount,
            advanceAmount: bookingData.advanceAmount,
          },
        };
        dispatch({ type: 'ADD_BOOKING', payload: newBooking });
        return newBooking;
      } catch (error) {
        console.error('Error creating booking:', error);
        if (useAPI) {
          throw new Error(error?.message || 'Failed to save booking to server. Please try again.');
        }

        const ground = getGroundById(booking.groundId);
        const service = getServiceById(booking.groundId, booking.serviceId);
        const totalAmount = calculateSlotPrice(booking.groundId, booking.serviceId, booking.slotId, booking.customPrice);
        const advanceAmount = booking.advanceAmount || 0;
        const balanceAmount = totalAmount - advanceAmount;
        const newBooking = {
          ...booking,
          groundName: ground?.name ?? 'Ground',
          serviceName: service?.name ?? 'Service',
          totalAmount,
          advanceAmount,
          balanceAmount,
          paymentStatus: balanceAmount > 0 ? 'pending' : 'fully_paid',
          id: Date.now(),
          status: 'confirmed',
          bookedBy: booking.contactName,
          bookedByPhone: booking.contactPhone,
        };
        dispatch({ type: 'ADD_BOOKING', payload: newBooking });
        return newBooking;
      }
    },
    [dispatch, useAPI, activeFacility, getGroundById, getServiceById, calculateSlotPrice]
  );

  const cancelBooking = useCallback(
    async (bookingId) => {
      try {
        if (useAPI) {
          const response = await bookingsAPI.cancel(bookingId);
          if (response.success) {
            dispatch({ type: 'CANCEL_BOOKING', payload: bookingId });
            return { success: true };
          }
          throw new Error(response.message || 'Failed to cancel booking');
        }
        dispatch({ type: 'CANCEL_BOOKING', payload: bookingId });
        return { success: true };
      } catch (error) {
        console.error('Error cancelling booking:', error);
        dispatch({ type: 'CANCEL_BOOKING', payload: bookingId });
        return { success: false, error: error.message };
      }
    },
    [dispatch, useAPI]
  );

  const archiveBooking = useCallback(
    (bookingId) => {
      dispatch({ type: 'ARCHIVE_BOOKING', payload: bookingId });
    },
    [dispatch]
  );

  const updateBookingStatus = useCallback(
    async (bookingId, status) => {
      try {
        if (useAPI) {
          const response = await bookingsAPI.update(bookingId, { status });
          if (response.success && response.data) {
            const updatedBooking = transformBookingFromAPI(response.data);
            dispatch({ type: 'UPDATE_BOOKING_STATUS', payload: { id: bookingId, status } });
            return { success: true, data: updatedBooking };
          }
          throw new Error(response.message || 'Failed to update booking status');
        }
        dispatch({ type: 'UPDATE_BOOKING_STATUS', payload: { id: bookingId, status } });
        return { success: true };
      } catch (error) {
        console.error('Error updating booking status:', error);
        dispatch({ type: 'UPDATE_BOOKING_STATUS', payload: { id: bookingId, status } });
        return { success: false, error: error.message };
      }
    },
    [dispatch, useAPI]
  );

  return { addBooking, cancelBooking, archiveBooking, updateBookingStatus };
}
